import type { AriaAttributes, ReactNode } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';

/**
 * A column of the field chooser, or one the host puts beside it (a preview): one scroller its
 * whole height, its header stuck to the top, with a rule under it once the content scrolls beneath.
 */
export function FieldChooserColumn({
  header,
  children,
  subtle = false,
  ...aria
}: {
  header: ReactNode;
  children: ReactNode;
  /** The grey ground of the preview. */
  subtle?: boolean;
} & Pick<AriaAttributes, 'aria-label' | 'aria-labelledby'>) {
  return (
    <section
      {...aria}
      className={cn('flex flex-col md:min-h-0', subtle && 'bg-bg-subtle')}
    >
      <ScrollShadowWrapper
        edge="soft"
        // the stuck header marks the top edge itself
        renderTopShadow={false}
        className="md:flex-1"
        // positioned: absolute bits inside (form controls' hidden inputs) scroll with the content
        scrollerClassName="relative with-styled-scrollbars"
      >
        <div
          className={cn(
            'sticky top-0 z-10 border-b border-transparent px-4 pt-4 pb-2 [[data-has-top-shadow]>&]:border-border-default',
            subtle ? 'bg-bg-subtle' : 'bg-bg-default'
          )}
        >
          {header}
        </div>
        <div className="flex flex-col gap-2 px-4 pt-1 pb-4">{children}</div>
      </ScrollShadowWrapper>
    </section>
  );
}
